import { useState } from "react";
import {
  Box,
  Button,
  Group,
  Modal,
  Stack,
  Textarea,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm, Controller } from "react-hook-form";
import { dateFormat } from "../../../../../utils/dateFormat";
import { useMutation } from "@tanstack/react-query";
import { changeFlagStatus } from "../../../../../services/flag";
import { cutDate } from "../../../../../utils/functions";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { setFlagData } from "../../../../../redux/features/flagDetails/flagDetailsSlice";

const ChangeStatusModal = ({ id, resolutionValue, disabled, locked, role }) => {
  const dispatch = useDispatch();

  const showDueDate =
    resolutionValue === "FOR_MONITORING" ||
    resolutionValue === "DOCUMENT_ENQUIRY";

  const [opened, setOpened] = useState(false);
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isValid },
  } = useForm({
    defaultValues: {
      dueDate: null,
      description: "",
      reviewJustification: "",
    },
  });

  const [dueDate, description, reviewJustification] = watch([
    "dueDate",
    "description",
    "reviewJustification",
  ]);

  const mutateResolution = useMutation({
    mutationKey: ["change-status", dueDate, description],
    mutationFn: () =>
      changeFlagStatus({
        id,
        resolutionStatus: resolutionValue,
        description,
        dueDate: cutDate(dueDate),
        reviewJustification,
      }),
    onError: () => {
      toast.error("Status change failed!");
    },
    onSuccess: ({ data }) => {
      dispatch(setFlagData(data));
      toast.success("Status Changed!");
    },
  });

  function handleCancel() {
    reset();
    setOpened(false);
  }

  async function onSubmit(values) {
    await mutateResolution.mutateAsync();
    reset();
    setOpened(false);
  }

  const showJustification =
    role === "AML_MANAGER" && resolutionValue === "FOR_MONITORING";

  return (
    <Box>
      <Button
        h={36}
        variant="outline"
        size="xs"
        onClick={() => setOpened(true)}
        disabled={disabled || locked}
      >
        Change Status
      </Button>

      <Modal
        opened={opened}
        onClose={handleCancel}
        title="Change Status"
        centered
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack>
            {showDueDate && (
              <Controller
                rules={{ required: showDueDate }}
                name="dueDate"
                control={control}
                render={({ field }) => (
                  <DatePickerInput
                    {...field}
                    valueFormat={dateFormat}
                    placeholder="Due date"
                    clearable
                  />
                )}
              />
            )}

            <Controller
              rules={{ required: true }}
              name="description"
              control={control}
              render={({ field }) => (
                <Textarea
                  {...field}
                  placeholder="Description"
                  minRows={4}
                  autosize
                />
              )}
            />

            {showJustification ? (
              <Controller
                rules={{ required: showJustification }}
                name="reviewJustification"
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    placeholder="Review Justification"
                    minRows={4}
                    autosize
                  />
                )}
              />
            ) : null}

            <Group justify="space-between">
              <Button color="red" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                loading={mutateResolution.isPending}
                variant="outline"
                type="submit"
                disabled={!isValid}
              >
                Confirm
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Box>
  );
};

export default ChangeStatusModal;
